import React from 'react';
import { Bell, Heart, MapPin, Store, Truck } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { displayLabelFor } from '../utils/deliveryLocationLabels';
import { focusRing } from '../styles/ui';

// Full-page "We don't deliver to this area yet" view, shown by StorefrontRoute
// in place of a storefront page once the backend has resolved the browsing
// location and no outlet delivers there. Presentation only: the location and
// its serviceability come from DeliveryLocationContext (the same state the
// header shows), and "Change Location" opens the existing location dialog.
//
// The artwork (right-hand scene, left-edge foliage) is cropped from the
// approved design reference and sits in public/unserviceable/. Both images are
// decorative: everything they say is also in the page text.

// The hero is laid out to the design's 1536px desktop measurements; the same
// content column simply stacks below xl.
const shell = 'mx-auto w-full max-w-[1250px] px-4 sm:px-6 lg:px-8 xl:px-0';

const REASONS = [
  {
    icon: Store,
    title: 'Expanding Soon',
    lines: ['We are adding new locations', 'every month.'],
    // Column insets follow the reference, whose three columns are not equal.
    className: 'xl:pl-[31px]',
  },
  {
    icon: Bell,
    title: 'Get Notified',
    lines: ['Be the first to know when', 'we start delivering in your area.'],
    className: 'xl:pl-[73px]',
  },
  {
    icon: Heart,
    title: 'Thank You',
    lines: ['Your support helps us', 'reach more homes.'],
    className: 'xl:pl-[60px]',
  },
];

const UnserviceableLocationPage = () => {
  const { label, pincode, openLocationPrompt } = useDeliveryLocation();
  const place = displayLabelFor({ label, pincode });

  return (
    <>
      <Navbar />
      <main data-testid="unserviceable-location" className="pt-20 bg-[#FBFDFB] text-emerald-950">
        {/* Hero band */}
        <section className="relative overflow-hidden bg-[linear-gradient(180deg,#FDFDFD_0%,#F8FCF9_40%,#F7FAF5_66%,#F8F9F4_94%,#FBFDFB_100%)]">
          <img
            src="/unserviceable/unserviceable-leaves-left.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none select-none hidden xl:block absolute left-0 top-0 h-full w-auto"
          />
          <div className={shell}>
            <div className="grid grid-cols-1 lg:grid-cols-[45fr_55fr] xl:grid-cols-[582px_1fr] gap-8 lg:gap-4 items-start xl:min-h-[565px]">
              <div className="pt-10 pb-8 sm:pt-12 lg:pt-[53px] lg:pb-14 xl:pl-[91px] xl:pb-0 max-w-[491px] xl:max-w-[582px]">
                <span className="inline-flex items-center gap-2 h-8 px-[14px] rounded-full bg-[#EEF6EE] text-[#154123] text-[12px] font-semibold shadow-[0_1px_2px_rgba(15,63,40,0.08)]">
                  <Truck size={15} strokeWidth={2} aria-hidden="true" />
                  Currently Unavailable
                </span>

                <h1 className="serif mt-[20px] text-[38px] sm:text-[44px] xl:text-[49px] font-bold leading-[1.12] tracking-[-0.01em] text-[#0F3F28]">
                  We don&apos;t deliver to
                  <br className="hidden xl:inline" /> this area yet
                </h1>

                <p className="mt-[12px] text-[18px] leading-[26px] text-[#667085]">
                  Sorry! We&apos;re not delivering to this location at the moment.
                  <br className="hidden xl:inline" /> But we&apos;re working hard to reach more areas soon.
                </p>

                <div className="mt-[28px] flex items-center gap-[17px] rounded-2xl border border-[#E7F1E6] bg-[#F1F8EE] px-3 py-3 shadow-[0_1px_2px_rgba(15,63,40,0.04)]">
                  <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-white text-[#1E7F3C] shadow-[0_1px_3px_rgba(15,63,40,0.08)]">
                    <MapPin size={24} strokeWidth={2} aria-hidden="true" />
                  </span>
                  <p className="min-w-0 text-[15px] leading-snug">
                    <span className="block font-bold text-[#0F3F28]">Your selected location</span>
                    <span className="block mt-0.5 text-[#475467] break-words">{place}</span>
                  </p>
                </div>

                <button
                  type="button"
                  onClick={openLocationPrompt}
                  className={`mt-[26px] flex w-full h-[52px] items-center justify-center gap-3 rounded-xl bg-[linear-gradient(90deg,#1B7F41_0%,#22883F_55%,#1B7F41_100%)] text-white text-[17px] font-semibold shadow-[0_6px_14px_rgba(27,127,65,0.28)] transition-[filter] hover:brightness-105 ${focusRing} focus-visible:rounded-xl`}
                >
                  <MapPin size={20} strokeWidth={2} aria-hidden="true" />
                  Change Location
                </button>
              </div>

              {/* Scene: in flow until xl, then pinned to the viewport's right
                  edge at the reference's size and position. */}
              <div className="lg:self-end">
                <img
                  src="/unserviceable/unserviceable-scene.png"
                  alt=""
                  aria-hidden="true"
                  width="786"
                  height="545"
                  className="pointer-events-none select-none block w-full max-w-[786px] mx-auto h-auto xl:absolute xl:right-0 xl:top-[20px] xl:mx-0 xl:w-[786px] xl:max-w-none"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reasons panel: one shared card, floating over the hero's edge. */}
        <section className={`${shell} relative -mt-2 xl:-mt-[19px] pb-[35px]`}>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.16fr_1fr] rounded-[20px] border border-[#EAF1EA] bg-white shadow-[0_10px_30px_rgba(15,63,40,0.06)]">
            {REASONS.map(({ icon: Icon, title, lines, className }, index) => (
              <div
                key={title}
                className={`relative flex items-center gap-6 px-6 py-6 xl:py-[34px] ${className}`}
              >
                {index > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-6 bottom-6 hidden md:block w-px bg-[#E8F1EA]"
                  />
                )}
                <span className="flex h-[74px] w-[74px] shrink-0 items-center justify-center rounded-full bg-[#E6F7E7] text-[#0E6A2B]">
                  <Icon size={32} strokeWidth={1.75} aria-hidden="true" />
                </span>
                <p className="text-[14px] leading-[21px] text-[#667085]">
                  <span className="block text-[16px] leading-[20px] font-bold text-[#0F3F28] mb-1.5">{title}</span>
                  {lines[0]}
                  <br className="hidden xl:inline" /> {lines[1]}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default UnserviceableLocationPage;
